import moment from 'moment';
import { Subject, takeUntil } from 'rxjs';

import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatDatepicker } from '@angular/material/datepicker';

import { ParentControlErrorStateMatcher } from '../../utilities';

@Component({
  selector: 'phoenix-date-control',
  template: `
    <mat-form-field class="w-full" [subscriptSizing]="subscriptSizing">
      <mat-label>{{ label }}</mat-label>
      <input
        name="dateControl"
        [formControl]="dateControl"
        matInput
        [matDatepicker]="datePicker"
        [max]="max"
        [min]="effectiveMin"
        [readonly]="readonly"
        [required]="isRequired"
        [phoenixMonthYearFormat]="monthAndYear"
        [attr.aria-label]="ariaLabel"
        [errorStateMatcher]="errorMatcher"
      />
      <mat-datepicker-toggle
        [disabled]="toggleDisabled"
        matSuffix
        [for]="datePicker"
      ></mat-datepicker-toggle>
      <mat-datepicker
        #datePicker
        [startView]="monthAndYear ? 'multi-year' : startView"
        (monthSelected)="
          monthAndYear ? setMonthAndYear($event, datePicker) : null
        "
      ></mat-datepicker>
      <mat-error
        ><small>{{ control | formError }}</small></mat-error
      >
    </mat-form-field>
  `,
  standalone: false,
})
export class DateControlComponent implements OnInit, OnChanges, OnDestroy {
  @Input() ariaLabel = '';
  @Input({
    required: true,
  })
  control = new FormControl(null as null | string);
  @Input() min: Date | string | null = null;
  @Input() max: Date | string | null = null;
  @Input() minToday: boolean = false;
  @Input() label = 'Date';
  @Input() toggleDisabled = false;
  @Input() readonly = false;
  @Input() subscriptSizing: 'fixed' | 'dynamic' = 'fixed';
  @Input() startView: 'year' | 'multi-year' | 'month' = 'month';
  @Input() monthAndYear = false;
  @Input() initToToday = false;

  dateControl = new FormControl(null as null | moment.Moment | string);
  errorMatcher: ErrorStateMatcher = new ErrorStateMatcher();
  private datepickerErrorKeys = new Set<string>();
  private readonly minTodayValue = moment.utc().startOf('day').toISOString();

  destroy$ = new Subject<void>();

  ngOnInit(): void {
    if (this.initToToday && !this.control.value) {
      const today = moment.utc().format('YYYY-MM-DDT00:00:00');
      this.control.setValue(today);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['control']) {
      this.destroy$.next();
      this.errorMatcher = new ParentControlErrorStateMatcher(this.control);

      this.dateControl.clearValidators();
      if (this.control.validator) {
        this.dateControl.setValidators(this.control.validator);
      }

      this.dateControl.updateValueAndValidity();

      this.dateControl.setValue(this.control.value);
      this.control.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
        if (!this.sameDateValue) {
          this.dateControl.setValue(this.control.value);
        }
      });

      this.dateControl.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.parseDate();
        });

      // Sync errors when dateControl validation status changes
      this.dateControl.statusChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.syncErrors();
        });

      this.control.disabled
        ? this.dateControl.disable()
        : this.dateControl.enable();
      this.control.statusChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(status => {
          if (status === 'DISABLED') {
            this.dateControl.disable();
          } else {
            this.dateControl.enable();
          }
        });
    }

    if (changes['min'] || changes['max']) {
      this.dateControl.updateValueAndValidity({ emitEvent: false });
      this.syncErrors();
    }
  }

  parseDate() {
    if (!this.sameDateValue) {
      this.control.markAsDirty();
      if (moment.isMoment(this.dateControl.value)) {
        this.control.setValue(moment.utc(this.dateControl.value).toISOString());
      } else {
        this.control.setValue(this.dateControl.value);
      }
    }
  }

  syncErrors(): void {
    const dateControlErrors = this.dateControl.errors;
    const controlErrors = this.control.errors;

    // Map Material datepicker error keys to our custom keys
    const mappedDateErrors: Record<string, unknown> = {};
    if (dateControlErrors) {
      if (dateControlErrors['matDatepickerMin']) {
        mappedDateErrors['minDate'] = dateControlErrors['matDatepickerMin'];
      }
      if (dateControlErrors['matDatepickerMax']) {
        mappedDateErrors['maxDate'] = dateControlErrors['matDatepickerMax'];
      }
      // Copy other errors from dateControl
      Object.keys(dateControlErrors).forEach(key => {
        if (key !== 'matDatepickerMin' && key !== 'matDatepickerMax') {
          mappedDateErrors[key] = dateControlErrors[key];
        }
      });
    }

    // Remove previous datepicker errors from control before merging
    const filteredControlErrors: Record<string, unknown> = {};
    if (controlErrors) {
      Object.keys(controlErrors).forEach(key => {
        if (!this.datepickerErrorKeys.has(key)) {
          filteredControlErrors[key] = controlErrors[key];
        }
      });
    }

    // Merge errors
    const mergedErrors = { ...filteredControlErrors, ...mappedDateErrors };
    const hasErrors = Object.keys(mergedErrors).length > 0;
    this.datepickerErrorKeys = new Set(Object.keys(mappedDateErrors));

    this.control.setErrors(hasErrors ? mergedErrors : null, {
      emitEvent: false,
    });
  }

  get sameDateValue(): boolean {
    return (
      this.control.value ===
      (moment.isMoment(this.dateControl.value)
        ? moment.utc(this.dateControl.value).toISOString()
        : this.dateControl.value)
    );
  }

  get isRequired(): boolean {
    if (!this.control.validator) {
      return false;
    }

    return this.control.hasValidator(Validators.required);
  }

  get effectiveMin(): Date | string | null {
    if (this.min) {
      return this.min;
    }

    return this.minToday ? this.minTodayValue : null;
  }

  setMonthAndYear(
    normalizedMonthAndYear: moment.Moment,
    datepicker: MatDatepicker<unknown>
  ) {
    if (this.monthAndYear) {
      this.dateControl.setValue(normalizedMonthAndYear);
      datepicker.close();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
